import InputField from "components/fields/InputField";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation } from "react-router-dom";
import { _resetPassword } from "Services/api";
import {
  ResetPasswordFormFieldsName,
  resetPasswordFormDefaultValues,
  resetPasswordFormSchema,
} from "constants/react-hook-form";

export default function ResetPassword() {
  const location = useLocation(); // Get the current location
  const queryParams = new URLSearchParams(location.search); // Parse the query parameters
  const token = queryParams.get("token"); // Extract the 'token' parameter

  const [generalError, setGeneralError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const { control, handleSubmit } = useForm({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: resetPasswordFormDefaultValues,
  });

  const handleResetPassword = async (data) => {
    // Reset errors and message
    setGeneralError("");
    setMessage("");

    setLoading(true);

    try {
      const response = await _resetPassword({
        body: { token, new_password: data[ResetPasswordFormFieldsName.PASSWORD] },
      });

      if (response.success) {
        setMessage("Password reset successfully! You can now log in.");
      } else {
        setGeneralError(response.message || "Failed to reset password");
      }
    } catch (error) {
      setGeneralError(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      {/* Loader Overlay */}
      {loading && (
        <div className="loader-overlay">
          <span className="loader"></span>
        </div>
      )}

      {/* Reset Password Section */}
      <form
        className="w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]"
        onSubmit={handleSubmit(handleResetPassword)}
        noValidate
      >
        <h4 className="mb-2.5 mt-5 text-4xl font-bold text-navy-700 dark:text-white">
          Reset Password
        </h4>

        {/* General Error or Success Message */}
        {generalError && <div className="mb-4 text-sm text-red-600">{generalError}</div>}
        {message && <div className="mb-4 text-sm text-green-600">{message}</div>}

        <InputField
          control={control}
          name={ResetPasswordFormFieldsName.PASSWORD}
          variant="auth"
          extra="mb-3"
          label="Enter your New Password"
          placeholder="Enter your New Password"
          id="password"
          type="password"
        />

        <InputField
          control={control}
          name={ResetPasswordFormFieldsName.CONFIRM_PASSWORD}
          variant="auth"
          extra="mb-3"
          label="Confirm your New Password"
          placeholder="Confirm your New Password"
          id="confirmPassword"
          type="password"
        />

        <button
          type="submit"
          className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
        >
          Submit
        </button>

        <div className="mt-4">
          <span className="text-sm font-medium text-navy-700 dark:text-gray-600">
            Already registered?
          </span>
          <Link
            to="/admin/auth/sign-in"
            className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
          >
            Login
          </Link>
        </div>
      </form>
    </div>
  );
}
